/**
 * Table — Tabela de dados reutilizável com dark theme
 */
import type { JSX } from 'preact';
import { T, CT } from '../../styles/tokens';
import { SkeletonBar } from './SkeletonBar';

interface Column {
  key: string;
  label: string;
  width?: string;
  render?: (val: any, row: any) => any;
}

interface TableProps {
  columns: Column[];
  data: any[];
  loading?: boolean;
  emptyText?: string;
  onRowClick?: (row: any) => void;
  striped?: boolean;
  style?: JSX.CSSProperties;
}

export function Table({ columns, data, loading, emptyText = 'Nenhum dado encontrado', onRowClick, striped, style }: TableProps) {
  return (
    <div style={{
      border: `1px solid ${CT.table.border}`,
      borderRadius: T.radiusLg,
      overflow: 'hidden',
      ...style,
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        background: CT.table.headerBg,
        borderBottom: `1px solid ${CT.table.border}`,
        padding: `${T.space3}px ${T.space4}px`,
      }}>
        {columns.map(col => (
          <div key={col.key} style={{
            flex: col.width ? `0 0 ${col.width}` : 1,
            fontSize: T.textXs,
            fontWeight: T.weightSemibold,
            color: T.textMuted,
            textTransform: 'uppercase' as const,
            letterSpacing: '0.5px',
          }}>
            {col.label}
          </div>
        ))}
      </div>

      {/* Body */}
      <div style={{ maxHeight: 400, overflowY: 'auto' }}>
        {loading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div key={i} style={{
              display: 'flex',
              padding: `${T.space3}px ${T.space4}px`,
              gap: T.space3,
              borderBottom: `1px solid ${CT.table.border}`,
            }}>
              {columns.map(col => (
                <div key={col.key} style={{ flex: col.width ? `0 0 ${col.width}` : 1 }}>
                  <SkeletonBar width="80%" height={12} />
                </div>
              ))}
            </div>
          ))
        ) : data.length === 0 ? (
          <div style={{
            padding: T.space8,
            textAlign: 'center',
            color: T.textMuted,
            fontSize: T.textSm,
          }}>
            {emptyText}
          </div>
        ) : (
          data.map((row, i) => (
            <div
              key={i}
              onClick={() => onRowClick?.(row)}
              style={{
                display: 'flex',
                padding: `${T.space3}px ${T.space4}px`,
                borderBottom: `1px solid ${CT.table.border}`,
                background: striped && i % 2 === 1 ? T.bgCard : 'transparent',
                cursor: onRowClick ? 'pointer' : 'default',
                transition: `background ${T.transitionFast}`,
              }}
              onMouseEnter={(e: any) => { e.currentTarget.style.background = CT.table.rowHover; }}
              onMouseLeave={(e: any) => { e.currentTarget.style.background = striped && i % 2 === 1 ? T.bgCard : 'transparent'; }}
            >
              {columns.map(col => (
                <div key={col.key} style={{
                  flex: col.width ? `0 0 ${col.width}` : 1,
                  fontSize: CT.table.fontSize,
                  color: T.text,
                  display: 'flex',
                  alignItems: 'center',
                }}>
                  {col.render ? col.render(row[col.key], row) : row[col.key]}
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
